<template>
  <a-modal
    :open="visible"
    title="覆盖缺口风险分析"
    width="70%"
    :footer="null"
    class="coverage-gaps-modal"
    @cancel="handleClose"
  >
    <div v-if="coverageGaps && coverageGaps.length > 0" class="coverage-gaps-content">
      <!-- 风险概览 -->
      <div class="mb-6 border border-red-200 rounded-lg bg-red-50 p-4">
        <h4 class="mb-3 text-lg font-semibold text-red-800">风险级别分布</h4>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div v-for="(count, level) in riskStats" :key="level" class="text-center">
            <div
              class="mx-auto mb-2 h-12 w-12 flex items-center justify-center rounded-full"
              :class="getRiskBadgeClass(level)"
            >
              <span class="font-bold text-white">{{ count }}</span>
            </div>
            <p class="text-xs uppercase text-gray-600">{{ getRiskLevelText(level) }}</p>
          </div>
        </div>
      </div>

      <!-- 详细风险列表 -->
      <div class="space-y-4">
        <h4 class="text-lg font-semibold text-gray-800">详细风险分析</h4>
        <div class="space-y-3">
          <div
            v-for="(gap, index) in coverageGaps"
            :key="`${gap.file_path}-${index}`"
            class="animate-fade-in-up border rounded-lg p-4"
            :class="getRiskContainerClass(gap.risk_level)"
            :style="{ animationDelay: `${index * 100}ms` }"
          >
            <div class="mb-3 flex items-start justify-between">
              <div class="flex-1">
                <div class="mb-2 flex items-center">
                  <span
                    class="mr-2 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium text-white"
                    :class="getRiskBadgeClass(gap.risk_level)"
                  >
                    {{ getRiskLevelText(gap.risk_level).toUpperCase() }}
                  </span>
                  <code class="text-sm font-mono text-gray-700">{{ getFileName(gap.file_path) }}</code>
                </div>
                <p class="mb-3 text-sm" :class="getRiskTextClass(gap.risk_level)">{{ gap.reason }}</p>
              </div>
            </div>

            <div class="border-t border-gray-200 pt-3">
              <p class="mb-2 text-xs font-medium text-gray-600">受影响的函数：</p>
              <div class="flex flex-wrap gap-1">
                <code
                  v-for="func in gap.functions"
                  :key="func"
                  class="inline-block border border-gray-200 rounded bg-white px-2 py-1 text-xs"
                >
                  {{ func }}
                </code>
              </div>
            </div>

            <div class="mt-3 flex items-center text-xs text-gray-600">
              <file-outlined class="mr-1" />
              {{ gap.file_path }}
            </div>
          </div>
        </div>
      </div>

      <!-- 改进建议 -->
      <div class="mt-6 border border-blue-200 rounded-lg bg-blue-50 p-4">
        <div class="mb-3 flex items-center">
          <bulb-outlined class="mr-2 text-blue-600" />
          <h5 class="font-semibold text-blue-800">整体改进建议</h5>
        </div>
        <ul class="text-sm text-blue-700 space-y-2">
          <li>
            • 优先处理
            <span class="font-semibold">Critical</span>
            和
            <span class="font-semibold">High</span>
            级别的风险缺口
          </li>
          <li>• 为未覆盖的核心业务函数编写专门的测试用例</li>
          <li>• 考虑使用集成测试来覆盖复杂的业务流程</li>
          <li>• 建立覆盖率监控机制，防止新代码引入覆盖缺口</li>
          <li>• 定期进行代码审查，确保测试用例的有效性</li>
        </ul>
      </div>
    </div>

    <div v-else class="py-8 text-center">
      <p class="text-gray-500">暂无覆盖缺口数据</p>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import type { CoverageGap } from '@/types/precision-new/ai-analysis';
import { BulbOutlined, FileOutlined } from '@ant-design/icons-vue';
import { computed } from 'vue';

// Props
interface Props {
  visible: boolean;
  coverageGaps?: CoverageGap[];
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  coverageGaps: () => [],
});

// Emits
const emit = defineEmits(['update:visible', 'close']);

// Computed
const riskStats = computed(() => {
  if (!props.coverageGaps) {
    return {};
  }

  return props.coverageGaps.reduce((stats: Record<string, number>, gap) => {
    stats[gap.risk_level] = (stats[gap.risk_level] || 0) + 1;
    return stats;
  }, {});
});

// Methods
function getRiskBadgeClass(level: string) {
  const colors = {
    critical: 'bg-red-600',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-blue-500',
  };
  return colors[level as keyof typeof colors] || 'bg-gray-500';
}

function getRiskContainerClass(level: string) {
  const colors = {
    critical: 'bg-red-50 border-red-300',
    high: 'bg-orange-50 border-orange-300',
    medium: 'bg-yellow-50 border-yellow-300',
    low: 'bg-blue-50 border-blue-300',
  };
  return colors[level as keyof typeof colors] || 'bg-gray-50 border-gray-300';
}

function getRiskTextClass(level: string) {
  const colors = {
    critical: 'text-red-800',
    high: 'text-orange-800',
    medium: 'text-yellow-800',
    low: 'text-blue-800',
  };
  return colors[level as keyof typeof colors] || 'text-gray-800';
}

function getRiskLevelText(level: string) {
  const texts = {
    critical: '严重',
    high: '高',
    medium: '中',
    low: '低',
  };
  return texts[level as keyof typeof texts] || level;
}

function getFileName(filePath: string) {
  return filePath.split('/').pop() || filePath;
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
}
</script>

<style scoped>
.coverage-gaps-modal :deep(.ant-modal-content) {
  max-height: 85vh;
}

.coverage-gaps-modal :deep(.ant-modal-body) {
  max-height: 70vh;
  overflow-y: auto;
}

.coverage-gaps-modal :deep(.ant-modal-body)::-webkit-scrollbar {
  width: 6px;
}

.coverage-gaps-modal :deep(.ant-modal-body)::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.coverage-gaps-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.coverage-gaps-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.5s ease-out;
}
</style>
